import { Modal, Table, ColumnDefinition, RowDefinition, useTableState } from '@indigo-ag/component-module';
import React from 'react';
import styled from 'styled-components';
import i18next from 'i18next';
import { currency } from 'shared';
import { Occurrence as SerasaOccurrence, SerasaOccurrence as SerasaOccurrenceType } from '@indigo-ag/schema';
import { date } from 'shared';
import { SerasaOccurrenceDetailsTable } from './SerasaOccurrenceDetailsTable';

const DetailModal = styled(Modal)`
  width: 80%;
  max-width: 1024px;
  padding-bottom: 16px;
  overflow: scroll;
  max-height: 600px;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TableContainer = styled.div`
  padding: 20px;
`;

type CellVariant = 'normal' | 'danger';

const TableCell = styled.div<{ variant?: CellVariant }>`
  font-size: 0.875rem;
  font-weight: 400;
  color: ${({ variant }) => (variant === 'danger' ? '#dc3545' : '#5b6b72')};
  overflow: visible;
  overflow-wrap: break-word;
  padding: 0.5rem 0.5rem 0.5rem 0;
`;

interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  occurrences: SerasaOccurrence[];
}

interface TableCellProps {
  label: string;
  variant?: CellVariant;
}

const Cell = ({ label, variant }: TableCellProps) => <TableCell variant={variant}>{label}</TableCell>;

export type ColumnId = keyof Omit<SerasaOccurrence, 'lastOccurrence'>;

const getCellVariant = (row: SerasaOccurrence): CellVariant => {
  const isBlockingRow = ['CHECK', 'NEGATIVE_SUMMARY'].includes(row.name);

  if (!isBlockingRow) return 'normal';

  return isBlockingRow && row.count > 0 ? 'danger' : 'normal';
};

const getOccurrenceDatesRender = (row: SerasaOccurrence) => {
  const { firstOccurrence, lastOccurrence } = row;
  const content =
    !!firstOccurrence && !!lastOccurrence
      ? `${date.formatDate(firstOccurrence)} - ${date.formatDate(lastOccurrence)}`
      : firstOccurrence;

  return <Cell label={content ?? '-'} variant={getCellVariant(row)} />;
};

const getOccurrenceTranslation = (occurrence: SerasaOccurrenceType) => {
  return i18next.t(`serasaOccurrence_${occurrence}`);
};

const columnDefinition: ColumnDefinition<SerasaOccurrence, ColumnId>[] = [
  {
    id: 'name',
    header: i18next.t('serasaRestrictionsDetailTable_occurrence'),
    cell: ({ row }) => <Cell label={getOccurrenceTranslation(row.name)} variant={getCellVariant(row)} />,
    constraints: {
      min: { rem: 6 },
      max: { fr: 2 }
    }
  },
  {
    id: 'count',
    header: i18next.t('serasaRestrictionsDetailTable_amount'),
    cell: ({ row }) => (
      <Cell
        label={row.count ? row.count.toString() : i18next.t('serasaRestrictionsDetailTable_amountValueZero')}
        variant={getCellVariant(row)}
      />
    ),
    constraints: {
      min: { rem: 5 },
      max: { fr: 1 }
    }
  },
  {
    id: 'firstOccurrence',
    header: i18next.t('serasaRestrictionsDetailTable_period'),
    cell: ({ row }) => getOccurrenceDatesRender(row),
    constraints: {
      min: { rem: 5 },
      max: { fr: 1.5 }
    }
  },
  {
    id: 'balance',
    header: i18next.t('serasaRestrictionsDetailTable_value'),
    cell: ({ row }) => (
      <Cell label={row?.balance ? currency.formatBrazilCurrency(row.balance) : '-'} variant={getCellVariant(row)} />
    ),
    constraints: {
      min: { rem: 5 },
      max: { fr: 1 }
    }
  }
];

const rowDefinition: RowDefinition<SerasaOccurrence, ColumnId> = {
  rowId: ({ row }) => row.name,
  expandable: ({ row, ColumnSpan }) => (
    <ColumnSpan>
      <SerasaOccurrenceDetailsTable details={row.details ?? []} />
    </ColumnSpan>
  )
};

export const SerasaRestrictionsDetailModal = ({ occurrences, isOpen, onRequestClose }: Props) => {
  const tableState = useTableState(occurrences);
  const content = (
    <TableContainer>
      <Table
        label="SerasaAnnotations"
        tableStyle="inline"
        columnDefinition={columnDefinition}
        rowDefinition={rowDefinition}
        {...tableState}
      />
    </TableContainer>
  );

  return (
    <DetailModal
      onRequestClose={onRequestClose}
      renderContent={() => content}
      open={isOpen}
      title={i18next.t('serasaRestrictionsDetailModal_title')}
    />
  );
};
